/**
 * iter313 — Tenant theme picker.
 *
 * Drop-in section for Admin → Business Settings → Branding. Loads
 * the 5 curated presets from `/api/admin/branding/themes` and the
 * tenant's current saved theme from `/api/admin/branding/theme`,
 * lets the admin pick a preset OR build a custom palette, previews
 * each swatch live, and saves via `PUT /api/admin/branding/theme`.
 *
 * The TenantContext (which already mounts on app load) will re-fetch
 * `/api/public/branding` and re-apply CSS custom properties on save,
 * so the rest of the dashboard updates without a hard reload.
 */
import { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Loader2, Check, Palette, RotateCcw } from 'lucide-react';
import { useTenant } from '../../context/TenantContext';

const PALETTE_FIELDS = [
  { key: 'primary',    label: 'Primary',         hint: 'Main action / brand colour. Used on primary buttons, links, focus rings.' },
  { key: 'primary_fg', label: 'On primary',      hint: 'Text colour shown on primary surfaces (e.g. button labels).' },
  { key: 'secondary',  label: 'Secondary',       hint: 'Secondary action colour. Used on chips, tags, muted buttons.' },
  { key: 'accent',     label: 'Accent',          hint: 'Highlight colour. Used in gradients, badges, decorations.' },
  { key: 'bg',         label: 'Page background', hint: 'The main page background. Pick dark for a dark theme, light for a light theme.' },
  { key: 'surface',    label: 'Surface',         hint: 'Card / sidebar / modal background. Should contrast slightly from the page bg.' },
  { key: 'text',       label: 'Body text',       hint: 'Default text colour on page background.' },
  { key: 'muted',      label: 'Muted',           hint: 'Secondary/disabled text + subtle borders.' },
  { key: 'success',    label: 'Success',         hint: 'Used for confirmation badges + green semantic states.' },
  { key: 'warn',       label: 'Warning',         hint: 'Used for caution badges + amber semantic states.' },
  { key: 'error',      label: 'Error',           hint: 'Used for error badges + destructive actions.' },
];

const HEX_RE = /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

export default function ThemePicker() {
  const { refresh: refetchTenant } = useTenant();
  const [presets, setPresets] = useState([]);
  const [saved, setSaved] = useState(null);
  const [resolved, setResolved] = useState(null);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [customPalette, setCustomPalette] = useState(null);
  const [customMode, setCustomMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [presetsRes, themeRes] = await Promise.all([
        api.get('/admin/branding/themes'),
        api.get('/admin/branding/theme'),
      ]);
      setPresets(presetsRes.data?.presets || []);
      const sv = themeRes.data?.saved || {};
      const rs = themeRes.data?.resolved || {};
      setSaved(sv);
      setResolved(rs);
      setSelectedPreset(sv.theme_id && sv.theme_id !== 'custom' ? sv.theme_id : (rs.theme_id || null));
      setCustomPalette(sv.custom_palette || rs.palette || null);
      setCustomMode(sv.theme_id === 'custom');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load themes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const previewPalette = useMemo(() => {
    if (customMode && customPalette) return customPalette;
    const preset = presets.find((p) => p.id === selectedPreset);
    return preset?.palette || resolved?.palette || null;
  }, [customMode, customPalette, selectedPreset, presets, resolved]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = customMode
        ? { theme_id: 'custom', custom_palette: customPalette }
        : { theme_id: selectedPreset };
      const { data } = await api.put('/admin/branding/theme', body);
      setSaved(data.saved);
      setResolved(data.resolved);
      toast.success('Theme saved — refreshing app colours…');
      // Reload tenant branding so CSS vars update without a hard reload.
      if (typeof refetchTenant === 'function') {
        await refetchTenant();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save theme');
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = async () => {
    if (!window.confirm('Revert to the default theme? Your custom palette will be cleared.')) return;
    setSaving(true);
    try {
      await api.delete('/admin/branding/theme');
      toast.success('Reverted to default theme');
      await load();
      if (typeof refetchTenant === 'function') await refetchTenant();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to revert');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card data-testid="theme-picker-loading">
        <CardContent className="py-10 flex items-center justify-center text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading themes…
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="theme-picker-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Palette className="h-5 w-5" />
          UI Theme
        </CardTitle>
        <CardDescription>
          Pick from one of 5 curated themes — or define your own palette for full control.
          Changes apply across your academy&apos;s admin dashboard and public landing pages.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* ===== PRESET GALLERY ===== */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <Label className="text-sm font-semibold">Presets</Label>
            {saved?.theme_id && (
              <Badge variant="outline" className="text-xs" data-testid="theme-current-badge">
                Current: {saved.theme_id === 'custom' ? 'Custom palette' : (presets.find((p) => p.id === saved.theme_id)?.name || saved.theme_id)}
              </Badge>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {presets.map((p) => {
              const isSelected = !customMode && selectedPreset === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => {
                    setCustomMode(false);
                    setSelectedPreset(p.id);
                    setCustomPalette(p.palette);
                  }}
                  data-testid={`theme-preset-${p.id}`}
                  className={`text-left rounded-lg border-2 p-3 transition-all hover:shadow-md ${
                    isSelected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-slate-200'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-sm">{p.name}</span>
                    {isSelected && <Check className="h-4 w-4 text-blue-600" />}
                  </div>
                  <div className="flex gap-1 mb-2">
                    {(p.preview_swatches || []).map((c, i) => (
                      <div key={i} className="w-6 h-6 rounded" style={{ background: c }} />
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 leading-snug">{p.description}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* ===== CUSTOM PALETTE TOGGLE ===== */}
        <div className="border-t pt-4">
          <button
            type="button"
            onClick={() => setCustomMode((v) => !v)}
            data-testid="theme-custom-toggle"
            className={`text-sm font-medium underline ${customMode ? 'text-blue-600' : 'text-slate-600'}`}
          >
            {customMode ? '↑ Hide custom palette editor' : '↓ Or build a custom palette'}
          </button>

          {customMode && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4" data-testid="theme-custom-grid">
              {PALETTE_FIELDS.map((f) => {
                const v = customPalette?.[f.key] || '#000000';
                const valid = HEX_RE.test(v);
                return (
                  <div key={f.key}>
                    <Label htmlFor={`palette-${f.key}`} className="text-xs">
                      {f.label}
                    </Label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="color"
                        value={valid ? v.slice(0, 7) : '#000000'}
                        onChange={(e) =>
                          setCustomPalette({ ...(customPalette || {}), [f.key]: e.target.value })
                        }
                        className="h-9 w-12 rounded border border-slate-300 cursor-pointer"
                        data-testid={`theme-color-${f.key}`}
                      />
                      <Input
                        id={`palette-${f.key}`}
                        value={v}
                        onChange={(e) =>
                          setCustomPalette({ ...(customPalette || {}), [f.key]: e.target.value })
                        }
                        className={`font-mono text-xs ${!valid ? 'border-red-400' : ''}`}
                      />
                    </div>
                    <p className="text-[10px] text-slate-400 mt-1 leading-tight">{f.hint}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ===== LIVE PREVIEW ===== */}
        {previewPalette && (
          <div className="border-t pt-4">
            <Label className="text-sm font-semibold mb-3 block">Live preview</Label>
            <div
              className="rounded-xl p-5 space-y-3"
              style={{ background: previewPalette.bg, color: previewPalette.text }}
              data-testid="theme-preview"
            >
              <div className="flex items-center gap-3">
                <div
                  className="rounded-lg px-4 py-2 text-sm font-semibold"
                  style={{ background: previewPalette.primary, color: previewPalette.primary_fg }}
                >
                  Primary button
                </div>
                <div
                  className="rounded-lg px-4 py-2 text-sm font-semibold border"
                  style={{ borderColor: previewPalette.muted, color: previewPalette.text }}
                >
                  Secondary
                </div>
                <span
                  className="text-xs px-2 py-1 rounded-full"
                  style={{ background: previewPalette.accent, color: previewPalette.primary_fg }}
                >
                  Accent badge
                </span>
              </div>
              <div
                className="rounded-lg p-4"
                style={{ background: previewPalette.surface, color: previewPalette.text }}
              >
                <div className="text-sm font-semibold mb-1">Surface card</div>
                <div className="text-xs" style={{ color: previewPalette.muted }}>
                  Subtle muted text — used for hints, captions, secondary info.
                </div>
                <div className="flex gap-2 mt-2 text-[10px] font-semibold uppercase">
                  <span className="px-2 py-0.5 rounded" style={{ background: previewPalette.success, color: '#fff' }}>OK</span>
                  <span className="px-2 py-0.5 rounded" style={{ background: previewPalette.warn, color: '#fff' }}>Warn</span>
                  <span className="px-2 py-0.5 rounded" style={{ background: previewPalette.error, color: '#fff' }}>Error</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===== ACTIONS ===== */}
        <div className="flex flex-wrap items-center gap-3 border-t pt-4">
          <Button onClick={handleSave} disabled={saving} data-testid="theme-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
            Save theme
          </Button>
          {saved?.theme_id && (
            <Button
              variant="outline"
              onClick={handleRevert}
              disabled={saving}
              data-testid="theme-revert-btn"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Revert to default
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
